import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { gql } from '@apollo/client';
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  alpha
} from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  WorkspacePremium as MedalIcon
} from '@mui/icons-material';
import apolloClient from 'apollo/client';

const EXERCISE_RANKINGS_QUERY = gql`
  query exerciseRankings($exercise_id: ID!, $period: RankingPeriod, $limit: Int, $gender: Gender) {
    exerciseRankings(exercise_id: $exercise_id, period: $period, limit: $limit, gender: $gender) {
      rank
      best_value
      value_type
      points
      medal
      formatted_value
      user {
        id
        full_name
        photo
      }
      exercise {
        id
        name
        localized_name
      }
    }
  }
`;

const EXERCISE_MONTH_RANKING_QUERY = gql`
  query exerciseMonthRanking($exercise_id: ID!, $month: Int!, $year: Int!, $limit: Int, $gender: Gender) {
    exerciseMonthRanking(exercise_id: $exercise_id, month: $month, year: $year, limit: $limit, gender: $gender) {
      rank
      best_value
      value_type
      formatted_value
      user {
        id
        full_name
        photo
      }
      exercise {
        id
        name
        localized_name
      }
    }
  }
`;

const medalColors = {
  gold: '#F9BD4E',
  silver: '#9aa0a6',
  bronze: '#9ca3af'
};

const selectSx = {
  color: '#e2e8f0',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#3f4448' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#F9BD4E' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#F9BD4E' },
  '& .MuiSvgIcon-root': { color: '#9aa0a6' }
};

const labelSx = {
  '& .MuiInputLabel-root': { color: '#9aa0a6' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#9aa0a6' }
};

function buildMonthOptions() {
  const options = [];
  const now = new Date();
  for (let i = 0; i < 6; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const month = d.getMonth() + 1;
    const year = d.getFullYear();
    const label = d.toLocaleString('es', { month: 'long', year: 'numeric' });
    options.push({ value: `${year}-${month}`, month, year, label: label.charAt(0).toUpperCase() + label.slice(1) });
  }
  return options;
}

function RankBadge({ rank }) {
  if (rank <= 3) {
    const Icon = rank === 1 ? TrophyIcon : MedalIcon;
    const color = [medalColors.gold, medalColors.silver, medalColors.bronze][rank - 1];
    return (
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Icon sx={{ color, fontSize: '1.2rem' }} />
        <Typography sx={{ color, fontWeight: 700, fontSize: '0.85rem' }}>{rank}</Typography>
      </Stack>
    );
  }
  return (
    <Typography sx={{ color: '#6b7177', fontWeight: 700, fontSize: '0.95rem', minWidth: 24, textAlign: 'center' }}>
      {rank}
    </Typography>
  );
}

RankBadge.propTypes = {
  rank: PropTypes.number.isRequired
};

function ExerciseRankingLeaderboard() {
  const exercises = useSelector((state) => state.exercise.data.exercises);
  const currentUserId = useSelector((state) => state.auth.user.id);

  const monthOptions = useMemo(() => buildMonthOptions(), []);
  // Default: current month
  const [selectedExercise, setSelectedExercise] = useState('');
  const [periodType, setPeriodType] = useState('month'); // 'month' | 'all_time'
  const [selectedMonth, setSelectedMonth] = useState(monthOptions[0].value);
  const [selectedGender, setSelectedGender] = useState(''); // '' = todos
  const [rankings, setRankings] = useState([]);
  const [loading, setLoading] = useState(false);

  const otrixverseExercises = exercises.filter((ex) => ex.is_otrixverse);

  const fetchRankings = useCallback(async (exerciseId, type, monthVal, genderVal) => {
    if (!exerciseId) return;
    setLoading(true);
    try {
      // '' → sin filtro de género (se envía null; el arg del backend es opcional).
      const gender = genderVal || null;
      if (type === 'all_time') {
        const { data } = await apolloClient.query({
          query: EXERCISE_RANKINGS_QUERY,
          variables: { exercise_id: exerciseId, period: 'ALL_TIME', limit: 50, gender },
          fetchPolicy: 'network-only'
        });
        setRankings(data?.exerciseRankings ?? []);
      } else {
        const option = monthOptions.find((o) => o.value === monthVal);
        if (!option) return;
        const { data } = await apolloClient.query({
          query: EXERCISE_MONTH_RANKING_QUERY,
          variables: { exercise_id: exerciseId, month: option.month, year: option.year, limit: 50, gender },
          fetchPolicy: 'network-only'
        });
        setRankings(data?.exerciseMonthRanking ?? []);
      }
    } catch {
      setRankings([]);
    } finally {
      setLoading(false);
    }
  }, [monthOptions]);

  useEffect(() => {
    if (selectedExercise) fetchRankings(selectedExercise, periodType, selectedMonth, selectedGender);
  }, [selectedExercise, periodType, selectedMonth, selectedGender, fetchRankings]);

  // Auto-select first exercise
  useEffect(() => {
    if (otrixverseExercises.length > 0 && !selectedExercise) {
      setSelectedExercise(String(otrixverseExercises[0].id));
    }
  }, [otrixverseExercises, selectedExercise]);

  return (
    <Box>
      {/* Filtros */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 280, ...labelSx }}>
          <InputLabel>Ejercicio</InputLabel>
          <Select
            value={selectedExercise}
            label="Ejercicio"
            onChange={(e) => setSelectedExercise(e.target.value)}
            sx={selectSx}
          >
            {otrixverseExercises.map((ex) => (
              <MenuItem key={ex.id} value={String(ex.id)}>
                {ex.localized_name || ex.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 160, ...labelSx }}>
          <InputLabel>Período</InputLabel>
          <Select
            value={periodType}
            label="Período"
            onChange={(e) => setPeriodType(e.target.value)}
            sx={selectSx}
          >
            <MenuItem value="month">Por mes</MenuItem>
            <MenuItem value="all_time">Histórico</MenuItem>
          </Select>
        </FormControl>

        {periodType === 'month' && (
          <FormControl size="small" sx={{ minWidth: 200, ...labelSx }}>
            <InputLabel>Mes</InputLabel>
            <Select
              value={selectedMonth}
              label="Mes"
              onChange={(e) => setSelectedMonth(e.target.value)}
              sx={selectSx}
            >
              {monthOptions.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <FormControl size="small" sx={{ minWidth: 160, ...labelSx }}>
          <InputLabel>Género</InputLabel>
          <Select
            value={selectedGender}
            label="Género"
            onChange={(e) => setSelectedGender(e.target.value)}
            sx={selectSx}
          >
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="MALE">Masculino</MenuItem>
            <MenuItem value="FEMALE">Femenino</MenuItem>
            <MenuItem value="OTHER">Otro</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {/* Tabla de ranking */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress sx={{ color: '#F9BD4E' }} />
        </Box>
      )}
      {!loading && rankings.length === 0 && (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <Typography sx={{ color: '#6b7177' }}>
            {selectedExercise
              ? 'No hay datos de ranking para este ejercicio en el período seleccionado.'
              : 'Selecciona un ejercicio para ver el ranking.'}
          </Typography>
        </Box>
      )}
      {!loading && rankings.length > 0 && (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { color: '#6b7177', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '1px solid #1F2327', pb: 1.5 } }}>
                <TableCell>#</TableCell>
                <TableCell>Atleta</TableCell>
                <TableCell align="right">Mejor marca</TableCell>
                {periodType === 'all_time' && <TableCell align="right">Puntos</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {rankings.map((entry) => {
                const isMe = String(entry.user?.id) === String(currentUserId);
                return (
                  <TableRow
                    key={entry.user?.id}
                    sx={{
                      '& td': { borderBottom: '1px solid #000000', py: 1.25 },
                      backgroundColor: isMe ? alpha('#F9BD4E', 0.06) : 'transparent',
                      '&:hover td': { backgroundColor: alpha('#F9BD4E', 0.04) }
                    }}
                  >
                    <TableCell sx={{ width: 40 }}>
                      <RankBadge rank={entry.rank} />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar
                          src={entry.user?.photo}
                          alt={entry.user?.full_name}
                          sx={{ width: 32, height: 32, fontSize: '0.8rem' }}
                        >
                          {entry.user?.full_name?.[0]}
                        </Avatar>
                        <Typography sx={{ color: isMe ? '#F9BD4E' : '#e2e8f0', fontWeight: isMe ? 700 : 400, fontSize: '0.9rem' }}>
                          {entry.user?.full_name}
                          {isMe && <Chip label="TÚ" size="small" sx={{ ml: 1, bgcolor: '#F9BD4E', color: '#000000', fontWeight: 700, height: 18, fontSize: '0.65rem' }} />}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Typography sx={{ color: '#F9BD4E', fontWeight: 600, fontSize: '0.9rem' }}>
                        {entry.formatted_value}
                      </Typography>
                    </TableCell>
                    {periodType === 'all_time' && (
                      <TableCell align="right">
                        <Typography sx={{ color: '#9aa0a6', fontSize: '0.85rem' }}>
                          {entry.points} pts
                        </Typography>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

export default ExerciseRankingLeaderboard;
